import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Eye, EyeOff, Mail, Lock, User, Building2,
  Phone, UserPlus, CheckCircle2, AlertCircle, Info,
} from 'lucide-react';
import api from '../lib/api';

/* ─── helpers ─── */
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRe = /^0[0-9]{8,9}$/;

function Field({
  label, hint, icon, children,
}: { label: string; hint?: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <label className="block">
        <span className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5">
          {label}
          {hint && <span className="text-gray-400 font-normal mr-1">({hint})</span>}
        </span>
        <div className="relative">{children}</div>
      </label>
    </div>
  );
}

const inputCls = (extra = '') =>
  `w-full rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800
   text-gray-900 dark:text-white px-4 py-3 text-sm focus:outline-none focus:ring-2
   focus:ring-blue-500 transition-all ${extra}`;

/* ─── Component ─── */
const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token') || '';

  /* referral: persist ?ref=CODE so attribution survives navigation, and show
     who referred the visitor. Falls back to a previously-stored code. */
  const [referrerName, setReferrerName] = useState('');
  useEffect(() => {
    const fromUrl = (searchParams.get('ref') || '').trim().toUpperCase();
    if (fromUrl) {
      try { localStorage.setItem('amutech_ref', fromUrl); } catch { /* ignore */ }
    }
    const code = fromUrl || (localStorage.getItem('amutech_ref') || '').toUpperCase();
    if (!code) return;
    api.get(`/referrers/resolve/${encodeURIComponent(code)}`)
      .then((r) => setReferrerName(r.data?.name || ''))
      .catch(() => { /* invalid/expired code — ignore silently */ });
  }, [searchParams]);

  /* form state */
  const [name, setName]               = useState('');
  const [phone, setPhone]             = useState('');
  const [orgName, setOrgName]         = useState('');
  const [orgEmail, setOrgEmail]       = useState('');
  const [orgEmailConfirm, setOrgEmailConfirm] = useState('');
  const [password, setPassword]       = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPass, setShowPass]       = useState(false);
  const [showPassC, setShowPassC]     = useState(false);

  /* ui state */
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState(false);

  /* ── validation ── */
  const validate = (): string | null => {
    if (!name.trim())              return 'שם מלא הוא שדה חובה';
    if (!phone.trim())             return 'מספר טלפון הוא שדה חובה';
    if (!phoneRe.test(phone.replace(/\s|-/g, ''))) return 'מספר טלפון לא תקין (לדוגמה: 0501234567)';
    if (!orgName.trim())           return 'שם העמותה הוא שדה חובה';
    if (!orgEmail.trim())          return 'מייל העמותה הוא שדה חובה';
    if (!emailRe.test(orgEmail))   return 'כתובת מייל לא תקינה';
    if (orgEmail !== orgEmailConfirm) return 'כתובות המייל אינן תואמות';
    if (password.length < 8)       return 'הסיסמה חייבת להכיל לפחות 8 תווים';
    if (password !== passwordConfirm) return 'הסיסמאות אינן תואמות';
    return null;
  };

  /* ── submit ── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const err = validate();
    if (err) { setError(err); return; }

    setSubmitting(true);
    try {
      const referralCode = ((searchParams.get('ref') || localStorage.getItem('amutech_ref') || '')).trim().toUpperCase();
      await api.post('/auth/register', {
        name:    name.trim(),
        email:   orgEmail.trim().toLowerCase(),
        phone:   phone.trim(),
        password,
        orgName: orgName.trim(),
        orgEmail: orgEmail.trim().toLowerCase(),
        ...(inviteToken ? { inviteToken } : {}),
        ...(referralCode ? { referralCode } : {}),
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'שגיאה בהרשמה. נסה שוב.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Success screen ── */
  if (success) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1565C0] via-[#311B92] to-[#6200EA] p-4">
        <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl p-10 max-w-md w-full text-center">
          <CheckCircle2 size={48} className="text-green-500 mx-auto mb-4 md:w-16 md:h-16" />
          <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white mb-2">נרשמת בהצלחה! 🎉</h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
            שלחנו לינק כניסה למייל <strong className="text-blue-600">{orgEmail}</strong>.<br />
            תקופת הניסיון שלך (7 ימים) התחילה.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3 rounded-full bg-gradient-to-l from-[#6200EA] to-[#1565C0] text-white font-bold text-base hover:opacity-90 transition-opacity"
          >
            כניסה למערכת
          </button>
        </div>
      </div>
    );
  }

  /* ── Form ── */
  return (
    <div dir="rtl" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1565C0] via-[#311B92] to-[#6200EA] p-4 relative overflow-hidden">
      {/* blobs */}
      <div className="absolute top-[-15%] right-[-10%] w-[40vw] h-[40vw] max-w-[480px] rounded-full bg-white/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-20%] left-[-10%] w-[50vw] h-[50vw] max-w-[600px] rounded-full bg-violet-400/10 blur-3xl pointer-events-none" />

      <div className="relative z-10 w-full max-w-lg bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden">
        {/* top bar */}
        <div className="h-1.5 w-full bg-gradient-to-l from-[#6200EA] to-[#1565C0]" />

        <div className="p-5 md:p-8">
          {/* logo */}
          <div className="text-center mb-6">
            <img src="/amutech-logo.png" alt="Amutech" className="h-16 w-auto mx-auto mb-2 object-contain" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">התחל ניסיון חינמי של 7 ימים</p>
          </div>

          {referrerName && (
            <div className="mb-4 flex items-center gap-2 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 px-4 py-2.5 text-sm text-green-700 dark:text-green-300">
              <CheckCircle2 size={16} className="shrink-0" />
              <span>הופנית על ידי <strong>{referrerName}</strong> 🎉</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">

            {/* שם מלא */}
            <Field label="שם מלא" icon={<User />}>
              <User size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="ישראל ישראלי"
                className={inputCls('pr-9')}
                required
              />
            </Field>

            {/* טלפון */}
            <Field label="טלפון נייד" hint="חובה">
              <Phone size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="0501234567"
                className={inputCls('pr-9')}
                style={{ direction: 'ltr', textAlign: 'right' }}
                required
              />
            </Field>

            {/* שם עמותה */}
            <Field label="שם העמותה">
              <Building2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                placeholder="עמותת דוגמה"
                className={inputCls('pr-9')}
                required
              />
            </Field>

            {/* מייל עמותה */}
            <Field label="מייל העמותה" hint="ישמש כשם משתמש לכניסה">
              <div className="flex items-start gap-1.5 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl px-3 py-2 mb-2">
                <Info size={13} className="flex-shrink-0 mt-0.5" />
                <span>כתובת המייל של העמותה תשמש כשם המשתמש שלך להתחברות</span>
              </div>
              <Mail size={16} className="absolute right-3 top-[calc(50%+14px)] -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="email"
                value={orgEmail}
                onChange={e => setOrgEmail(e.target.value)}
                placeholder="office@org.org.il"
                className={inputCls('pr-9')}
                style={{ direction: 'ltr', textAlign: 'right' }}
                required
              />
            </Field>

            {/* וידוא מייל */}
            <Field label="אישור מייל">
              <Mail size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="email"
                value={orgEmailConfirm}
                onChange={e => setOrgEmailConfirm(e.target.value)}
                placeholder="חזור על כתובת המייל"
                className={inputCls('pr-9')}
                style={{ direction: 'ltr', textAlign: 'right' }}
                required
              />
              {orgEmailConfirm && (
                <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-xs ${orgEmail === orgEmailConfirm ? 'text-green-500' : 'text-red-500'}`}>
                  {orgEmail === orgEmailConfirm ? '✓' : '✗'}
                </span>
              )}
            </Field>

            {/* סיסמה */}
            <Field label="סיסמה" hint="לפחות 8 תווים">
              <Lock size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type={showPass ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="לפחות 8 תווים"
                className={inputCls('pr-9 pl-10')}
                style={{ direction: 'ltr', textAlign: 'right' }}
                minLength={8}
                required
              />
              <button type="button" onClick={() => setShowPass(v => !v)}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600">
                {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </Field>

            {/* וידוא סיסמה */}
            <Field label="אישור סיסמה">
              <Lock size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type={showPassC ? 'text' : 'password'}
                value={passwordConfirm}
                onChange={e => setPasswordConfirm(e.target.value)}
                placeholder="חזור על הסיסמה"
                className={inputCls('pr-9 pl-10')}
                style={{ direction: 'ltr', textAlign: 'right' }}
                required
              />
              <button type="button" onClick={() => setShowPassC(v => !v)}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600">
                {showPassC ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              {passwordConfirm && (
                <span className={`absolute left-10 top-1/2 -translate-y-1/2 text-xs ${password === passwordConfirm ? 'text-green-500' : 'text-red-500'}`}>
                  {password === passwordConfirm ? '✓' : '✗'}
                </span>
              )}
            </Field>

            {/* error */}
            {error && (
              <div className="flex items-start gap-2 bg-red-50 dark:bg-red-950/30 border border-red-200 rounded-xl px-4 py-3 text-red-700 dark:text-red-400 text-sm">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-gradient-to-l from-[#6200EA] to-[#1565C0] text-white font-bold text-base shadow-lg hover:opacity-90 active:opacity-80 transition-all disabled:opacity-50 mt-2"
            >
              {submitting
                ? <div className="w-5 h-5 rounded-full border-2 border-white border-t-transparent animate-spin" />
                : <><UserPlus size={18} /> צור חשבון ניסיון חינמי</>
              }
            </button>

            <p className="text-center text-xs text-gray-400 mt-2">
              כבר יש לך חשבון?{' '}
              <Link to="/login" className="text-blue-600 hover:underline font-semibold">כניסה</Link>
            </p>
          </form>
        </div>

        <p className="text-center text-xs text-gray-300 dark:text-gray-600 pb-4">
          מערכת ניהול מאובטחת · Amutech © 2026
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
